import { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { getAdminBackgroundChecks } from '@/lib/api';
import { toast } from 'sonner';
import {
  UserCheck, Clock, CheckCircle2, AlertTriangle, XCircle,
  Search, Filter, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const STATUS_CONFIG = {
  invited: { label: 'Invited', icon: Clock, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  processing: { label: 'Processing', icon: RefreshCw, color: 'text-rtb-purple', bg: 'bg-rtb-purple/10', border: 'border-rtb-purple/20' },
  clear: { label: 'Cleared', icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  review_needed: { label: 'Needs Review', icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  failed: { label: 'Failed', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
};

export default function AdminBackgroundChecks() {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchChecks = async () => {
    try {
      const data = await getAdminBackgroundChecks(filter === 'all' ? null : filter);
      setChecks(data);
    } catch {
      toast.error('Failed to load background checks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchChecks(); }, [filter]);

  const filtered = checks.filter(c =>
    !search || c.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.user_email?.toLowerCase().includes(search.toLowerCase())
  );

  const statusCounts = checks.reduce((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <AdminLayout title="Background Checks">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <button
              key={key}
              onClick={() => setFilter(filter === key ? 'all' : key)}
              className={`p-4 rounded-xl border transition-all ${
                filter === key ? `${cfg.bg} ${cfg.border}` : 'bg-white/[0.03] border-white/[0.06] hover:border-white/[0.12]'
              }`}
              data-testid={`filter-${key}`}
            >
              <Icon className={`w-5 h-5 ${cfg.color} mb-2`} />
              <p className="text-2xl font-oswald font-bold text-white">{statusCounts[key] || 0}</p>
              <p className="text-white/40 text-xs">{cfg.label}</p>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="input-dark pl-10"
          data-testid="bg-check-search"
        />
      </div>

      {/* Setup Instructions */}
      {checks.length === 0 && !loading && (
        <div className="p-8 bg-white/[0.03] border border-white/[0.06] rounded-xl text-center" data-testid="checkr-setup-guide">
          <UserCheck className="w-12 h-12 text-rtb-purple mx-auto mb-4" />
          <h3 className="font-oswald text-xl font-bold text-white mb-2">Set Up Checkr Background Checks</h3>
          <p className="text-white/50 mb-6 max-w-md mx-auto">
            Background checks will auto-initiate when participants sign their contract.
          </p>
          <div className="text-left max-w-lg mx-auto space-y-3">
            <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg">
              <span className="w-6 h-6 bg-rtb-purple/20 text-rtb-purple rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
              <div>
                <p className="text-white/80 text-sm font-medium">Create a Checkr account</p>
                <a href="https://checkr.com" target="_blank" rel="noopener noreferrer" className="text-rtb-purple text-xs underline">checkr.com</a>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg">
              <span className="w-6 h-6 bg-rtb-purple/20 text-rtb-purple rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
              <div>
                <p className="text-white/80 text-sm font-medium">Get your Secret API Key</p>
                <p className="text-white/40 text-xs">Dashboard &rarr; Account Settings &rarr; Developer Settings</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg">
              <span className="w-6 h-6 bg-rtb-purple/20 text-rtb-purple rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
              <div>
                <p className="text-white/80 text-sm font-medium">Choose a screening package</p>
                <p className="text-white/40 text-xs">Basic Criminal, Standard, or Professional — configure in Settings</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg">
              <span className="w-6 h-6 bg-rtb-purple/20 text-rtb-purple rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
              <div>
                <p className="text-white/80 text-sm font-medium">Add your API key in Settings</p>
                <p className="text-white/40 text-xs">Go to Admin Settings &rarr; Background Checks section and paste your key</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg">
              <span className="w-6 h-6 bg-rtb-purple/20 text-rtb-purple rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">5</span>
              <div>
                <p className="text-white/80 text-sm font-medium">Set up the webhook URL in Checkr Dashboard</p>
                <p className="text-white/40 text-xs break-all">{process.env.REACT_APP_BACKEND_URL}/api/webhooks/checkr</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Checks Table */}
      {filtered.length > 0 && (
        <div className="space-y-2">
          {filtered.map(check => {
            const cfg = STATUS_CONFIG[check.status] || STATUS_CONFIG.invited;
            const Icon = cfg.icon;
            return (
              <div
                key={check.id}
                className="p-4 bg-white/[0.03] border border-white/[0.06] rounded-xl flex items-center gap-4 hover:border-white/[0.12] transition-all"
                data-testid={`bg-check-${check.id}`}
              >
                <div className={`w-10 h-10 ${cfg.bg} rounded-lg flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${cfg.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{check.user_name}</p>
                  <p className="text-white/40 text-sm truncate">{check.user_email}</p>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${cfg.bg} ${cfg.border} border rounded-full text-xs font-medium ${cfg.color}`}>
                    {cfg.label}
                  </span>
                  <p className="text-white/30 text-xs mt-1">
                    {check.initiated_at ? new Date(check.initiated_at).toLocaleDateString() : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center h-40">
          <div className="w-8 h-8 border-2 border-rtb-purple border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </AdminLayout>
  );
}
